// The main-thread time slicer (MAPFIRST, APP_PLAYER §7). Long build loops and
// the decode stages call `await slice()`: it returns at once while the
// current slice is within its budget, and yields to the browser (input,
// paint, the Map) when it is not.
//
// THE FRAME BUDGET (R8d): ONE set of numbers per device class, renderconfig
// device_budgets.<class>.frame (read by the client and the gates; desktop
// measured, Quest / phone the stated defaults until the owner's bench run):
//   ms {map, beam, play}  the slice per phase main.js sets —
//        'map'  before entry: the 2D Map must stay smooth while the 3D world
//               loads behind it; 'beam' from the ⤳ Beam click to the beam-in
//               latch (the overlay is up; the near ground wants the CPU);
//               'play' afterwards (one frame at most)
//   compile_ms, upload_kb the compile queue's share (compileq.js: compile()
//        calls per task, texture bytes uploaded per frame)
//   xr_share   IN A HEADSET every phase's budget is capped to this share of
//        the XR frame period (setXR, main.js on the WebXR sessionstart /
//        sessionend and frameratechange): the slicer does not run on
//        requestAnimationFrame — it yields between tasks, so it keeps running
//        inside an XR session — and a 16 ms slice is longer than a 72 Hz
//        (13.9 ms) or 90 Hz (11.1 ms) frame. 0.35 x the period: 3.9 ms at
//        90 Hz, 4.9 ms at 72 Hz (a rate outside [72, 144] Hz, or none
//        reported, counts as the nearest bound / 90 Hz). THE FRAME ROOM
//        (vr-smooth, 2026-11-01): the cap alone is blind to the frame's phase —
//        a slice that starts 2 ms before the next XR callback runs 4.9 ms into
//        it and the frame lands a period late (session 872, Quest 3: the 2- vs
//        3-period mix the owner saw as jitter). main.js tick marks every XR
//        frame (xrFrame(start, end)); the slicer predicts the next callback —
//        start + n x the period, n = the periods the frame's CPU part filled
//        (+ XR_POST_MS of the browser's own submit) — and a slice must yield
//        XR_GUARD_MS before it, never run into it: the budget is the room left,
//        not the share, and inside the guard the slicer sleeps past the
//        predicted callback (a timer hop, not scheduler.yield, which would run
//        ahead of the frame). A callback that does not come moves the
//        prediction one period on (XR_AFTER_MS of start jitter allowed). Without
//        a mark (the first frame, a gate without one) the share alone holds.
// Before the class is known (main.js setFrameClass after detectDevice) the
// renderconfig frame_budget.before_class numbers apply; a class without a
// budget of its own (software) reads frame_budget.alias.
//
// A worker executor later replaces the decode half (client/src/decode.js);
// the build half keeps slicing here. No DOM at module scope (Node: the gates).
//
// The long-task observer (PerformanceObserver 'longtask', Chromium) counts
// tasks ≥ 50 ms, split at the Beam click (setPhase('beam')): the load report's
// `long: {pre, post}`; each task is attributed to the activity mark (`act`)
// covering most of it: the report's `lt_top` / `lt_by`.
import RENDER_CFG from './renderconfig.json' with { type: 'json' };

const FB = RENDER_CFG.frame_budget;
// a device class -> its frame budget (renderconfig device_budgets.<class>.frame)
export function frameOf(cls) {
  const B = RENDER_CFG.device_budgets;
  const c = (FB.alias && FB.alias[cls]) || cls;
  return ((B[c] && B[c].frame) || B[FB.before_class].frame);
}
let _frame = frameOf(FB.before_class);
let _frameCls = null;

let _phase = 'map';
let _budget = _frame.ms.map;
let _start = -1e9;              // start of the current slice
let _last = -1e9;               // last slice() call
let _nYield = 0;                // yields so far (the gate counts them)
const _sysNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
let _now = _sysNow;
// the gate's seam: a fake clock (null = the real one) → deterministic slicing
// (a new clock starts a fresh slice timeline: a stamp of the old clock may
// lie in the new one's future, and then nothing would ever yield)
export function setClock(fn) { _now = typeof fn === 'function' ? fn : _sysNow; _start = _last = -1e9; }
export const yieldCount = () => _nYield;

// yield one task: scheduler.yield (keeps our place in line) > a MessageChannel
// hop (no 4 ms clamp) > setTimeout / setImmediate (Node). scheduler.yield's
// continuation is queued AHEAD of ordinary tasks, so a long sliced chain on
// it starves fetch / decode completions (the Map's photo tiles): in the
// 'map' phase, after HOP_MS of continuous slicing the next yield is a
// MessageChannel hop instead, which queues BEHIND them (APP_STARTUP "Open
// device check").
const HOP_MS = 50;
let _yield;
let _hop = null;
if (typeof MessageChannel !== 'undefined' && typeof window !== 'undefined') {
  const ch = new MessageChannel();
  const q = [];
  ch.port1.onmessage = () => { const f = q.shift(); if (f) f(); };
  _hop = () => new Promise((res) => { q.push(res); ch.port2.postMessage(0); });
}
let _chainAt = -1e9;            // start of the current run of back-to-back yields
if (typeof scheduler !== 'undefined' && scheduler && typeof scheduler.yield === 'function') {
  _yield = () => {
    const t = _now();
    if (t - _last > 2 * _budget + 4) _chainAt = t;   // a real gap: a new chain
    if (_hop && _phase === 'map' && t - _chainAt >= HOP_MS) { _chainAt = t; return _hop(); }
    return scheduler.yield();
  };
} else if (_hop) {
  _yield = _hop;
} else if (typeof setImmediate === 'function') {
  _yield = () => new Promise((res) => setImmediate(res));
} else {
  _yield = () => new Promise((res) => setTimeout(res, 0));
}

export function setPhase(p) {
  if (!(p in _frame.ms)) return;
  if (p === 'beam') markBeam();
  _phase = p;
  _budget = Math.min(_frame.ms[p], _xrCap);
}
// the device class's frame budget from now on (main.js, once detectDevice
// named it); frame() = the numbers in force, frameClass() = whose (null: before)
export function setFrameClass(cls) {
  _frame = frameOf(cls);
  _frameCls = cls || null;
  if (_xrCap !== Infinity) _xrCap = xrBudget(_xrHz);
  _budget = Math.min(_frame.ms[_phase], _xrCap);
}
export const frame = () => _frame;
export const frameClass = () => _frameCls;
export const phase = () => _phase;
export const budget = () => _budget;

// ---- QUIET START (owner ruling 2026-10-17, smooth Map): while the Map is
// up and nothing is chosen yet, the 3D side does nothing heavy — no shader
// compile / warm-up, no landing draw, no ground texture
// pool allocation or copy. main.js allowHeavy() at the first card
// (prefetchAt) or the ⤳ Beam (enterAt); downloads never wait for it.
// heavyReady(): resolves then (main.js: the structure draw and the TLM
// object mesh build await it).
let _heavy = false, _heavyGo = null;
const _heavyP = new Promise((res) => { _heavyGo = res; });
export const heavyOk = () => _heavy;
export const heavyReady = () => _heavyP;
export function allowHeavy() {
  if (_heavy) return;
  _heavy = true;
  _heavyGo();
}

// ---- HIDDEN (gs-gpupainter p6 rulings (a) / (b)(ii)): the world is behind the beam-in overlay —
// from the ⤳ Beam (main.js showBeamIn) until the overlay drops (hideBeamIn: the flat / phone
// overlay's fade-out starts, VR's black quad goes in the same tick), no frame of it shown. NOT the
// phase: the latch (setPhase('play')) comes first and the overlay holds on past it (the first view
// warm, the smooth frames, VR's vegetation rule). While it holds, the ground painter's run caps are
// x BEAM_K (groundgen runCaps) and the governor's gen factor floors at 1 (gensched genFactors);
// from the first shown frame on, neither.
let _hidden = false;
export const hidden = () => _hidden;
export function setHidden(on) { _hidden = !!on; }

// ---- the XR cap (see the header): Infinity = no immersive session
const XR_DEFAULT_HZ = 90, XR_MIN_HZ = 72, XR_MAX_HZ = 144;
let _xrCap = Infinity;
let _xrHz;
// the slice budget (ms) for an XR frame rate hz (XRSession.frameRate;
// undefined / not finite: 90 Hz)
export function xrBudget(hz) {
  const f = Number.isFinite(hz) && hz > 0 ? Math.min(XR_MAX_HZ, Math.max(XR_MIN_HZ, hz)) : XR_DEFAULT_HZ;
  return _frame.xr_share * 1000 / f;
}
// on: an immersive session presents (its frame rate when known); off: the
// phase budgets as above
export function setXR(on, hz) {
  _xrHz = hz;
  _xrCap = on ? xrBudget(hz) : Infinity;
  _budget = Math.min(_frame.ms[_phase], _xrCap);
  if (!on) _xfStart = _xfEnd = -1e9;
}
export const xrOn = () => _xrCap !== Infinity;

// ---- THE FRAME ROOM (header): the XR frame mark + the predicted next callback
export const XR_GUARD_MS = 1.5;   // a slice yields this long before the predicted callback
export const XR_POST_MS = 1.0;    // the browser's own work after our callback before the frame submits
export const XR_AFTER_MS = 2.5;   // a callback may start this late past its vsync before it counts as missed
let _xfStart = -1e9, _xfEnd = -1e9;
// main.js tick, after its draw: this XR frame's callback start and the end of
// its CPU part (the render done) — the slicer's phase reference
export function xrFrame(start, end) {
  if (!(end >= start)) return;
  _xfStart = start; _xfEnd = end;
}
const xrPeriod = () => 1000 / (Number.isFinite(_xrHz) && _xrHz > 0 ? Math.min(XR_MAX_HZ, Math.max(XR_MIN_HZ, _xrHz)) : XR_DEFAULT_HZ);
// the predicted start of the next XR frame callback at time t (Infinity: no
// session or no mark yet): the frame's CPU part + the browser's post fills n
// whole periods; a predicted callback XR_AFTER_MS gone without a frame mark
// counts as missed and the prediction moves to the next vsync
export function xrNextFrame(t = _now()) {
  if (_xrCap === Infinity || !(_xfEnd >= _xfStart) || _xfStart < -1e8) return Infinity;
  const p = xrPeriod();
  let g = _xfStart + Math.max(1, Math.ceil((_xfEnd - _xfStart + XR_POST_MS) / p)) * p;
  if (g + XR_AFTER_MS <= t) g += Math.ceil((t - g - XR_AFTER_MS) / p + 1e-9) * p;
  return g;
}
// the ms a between-frames task may still run at t before the guard (Infinity
// outside a session / before the first mark): the compile queue's task bound
// (main.js → compileq.js room), the slicer's deadline
export function xrRoom(t = _now()) {
  const g = xrNextFrame(t);
  return g === Infinity ? Infinity : g - XR_GUARD_MS - t;
}
// a timer hop past the predicted callback (the real clock); under a fake
// clock (the gates) a plain yield — the gate advances its own clock
const _sleep = (ms) => (_now !== _sysNow ? _yield() : new Promise((res) => setTimeout(res, Math.max(0, ms))));

// await slice(): yield when the current slice ran past the phase's budget.
// A gap past the budget since the last call yields AT ONCE: either one stage
// ran that long between two calls (the frame is already late — never start
// another stage in the same task), or other tasks ran in between (then the
// hop costs one cheap task). Either way the next slice starts fresh.
export async function slice() {
  const t = _now();
  const g = xrNextFrame(t);                       // the frame room (header): Infinity outside XR
  if (g !== Infinity && t > g - XR_GUARD_MS) {
    // inside the guard: the frame goes first — sleep past its predicted start
    _nYield++;
    await _sleep(g + XR_AFTER_MS - t);
    _start = _last = _now();
    return;
  }
  if (t - _last < _budget && t - _start < _budget) { _last = t; return; }
  _nYield++;
  await _yield();
  _start = _last = _now();
}

// true once the current slice ran its budget or reached the frame guard: a
// step loop (decode.js runSteps) checks it between steps and stops there to
// `await slice()` (which then yields). The same clock and budget as slice().
export function due() { const t = _now(); return t - _start >= _budget || t > xrNextFrame(t) - XR_GUARD_MS; }

// a loop helper: f(i) for i in [0, n), slicing every `every` iterations
export async function sliced(n, f, every = 256) {
  for (let i = 0; i < n; i++) {
    f(i);
    if (i % every === every - 1) await slice();
  }
}

// ---- activity marks (load-report-pre-entry): what the main thread is doing
// A long-task entry arrives AFTER its task, without a word about what ran, so
// the known seams leave cheap time-stamped marks and the observer matches each
// task against the marks inside it:
//   act(label)      a point: "from here this task is doing <label>" — until the
//                   next mark or the end of the task (fetchAsset completions:
//                   the caller's continuation runs in the same task)
//   act(label, fn)  a span: fn runs synchronously under <label>, then an
//                   end mark ('') closes it (the decoders, the ring build,
//                   the shader warm-up parts)
// A task is attributed to the label covering most of it (`at` = those ms);
// none = 'other'. Labels are short tokens ([A-Za-z0-9_.:#/-], ≤ 48 chars —
// the gs.js cleanLoad rule); actLabel() makes one from an asset URL.
const ACT_CAP = 4096;         // marks kept (a burst of ~170 tile fetches is far below)
const _acts = [];             // [{l, t}] in time order
export function act(label, fn) {
  _acts.push({ l: label, t: _now() });
  if (_acts.length > ACT_CAP) _acts.splice(0, ACT_CAP >> 1);
  if (typeof fn !== 'function') return undefined;
  try { return fn(); } finally { _acts.push({ l: '', t: _now() }); }
}
// `/bake/<id>/terrain/L0/3_4.bin?x` → `terrain/L#/#_#.bin` (digit runs → #:
// the class of artifact, not the tile; a content name's sha16 — the ring
// tiers' and tile variants' `<prefix>-<sha16>` files — → # too), prefixed
export function actLabel(prefix, url) {
  let p = String(url || '').replace(/[?#].*$/, '');
  const i = p.indexOf('/bake/');
  if (i >= 0) { p = p.slice(i + 6); p = p.slice(p.indexOf('/') + 1); }
  p = p.replace(/^\/+/, '').replace(/(^|[/-])[0-9a-f]{16}(?=\.)/g, '$1#').replace(/\d+/g, '#').replace(/[^A-Za-z0-9_.#/-]/g, '_');
  return (prefix + ':' + p).slice(0, 48);
}
// The label of the task [s, s+d]: the marks inside it cut it into segments
// (each mark's label holds until the next mark or the task end; the part
// before the first mark is unknown); the longest labelled segment wins.
// Marks are sorted by t. Pure (the gate runs it on fixtures).
export function attributeTask(marks, s, d) {
  const e = s + d;
  const by = new Map();
  let lo = 0, hi = marks.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (marks[m].t < s - 1) lo = m + 1; else hi = m; }
  for (let i = lo; i < marks.length && marks[i].t <= e + 1; i++) {
    const l = marks[i].l;
    if (!l) continue;
    const t0 = Math.max(s, marks[i].t);
    const t1 = Math.min(e, i + 1 < marks.length ? marks[i + 1].t : e);
    by.set(l, (by.get(l) || 0) + Math.max(0, t1 - t0));
  }
  let best = null, at = 0;
  for (const [l, ms] of by) if (best === null || ms > at) { best = l; at = ms; }
  return { l: best || 'other', at: best ? at : 0 };
}

// ---- long tasks
let _splitAt = Infinity;      // the Beam click (performance.now ms)
const _long = { pre: { n: 0, total_ms: 0, max_ms: 0 }, post: { n: 0, total_ms: 0, max_ms: 0 } };
const TOP_N = 10;             // the longest tasks the report lists
const BY_N = 8;               // the labels the report sums
const _top = [];              // [{l, ms, t, at}] longest first, ≤ TOP_N
const _byLabel = new Map();   // label → {n, ms}, every long task
const _byWin = new Map();     // label → {n, ms}, post-entry long tasks since the last longTaskWindow()
let _obs = null;
let _ltHook = null;           // the device bench's listener (bench.js longTask), else null
// the device bench (bench.js): every long task (start, ms, label) as it is attributed
export function onLongTask(fn) { _ltHook = typeof fn === 'function' ? fn : null; }
// one long-task entry ({startTime, duration}): the {pre, post} split + the
// attribution (exported: the gate feeds fixtures through it)
export function noteLongTask(e) {
  // a task that started before the Beam click counts as before entry
  const o = e.startTime >= _splitAt ? _long.post : _long.pre;
  o.n++;
  o.total_ms += e.duration;
  if (e.duration > o.max_ms) o.max_ms = e.duration;
  const { l, at } = attributeTask(_acts, e.startTime, e.duration);
  const b = _byLabel.get(l) || { n: 0, ms: 0 };
  b.n++; b.ms += e.duration;
  _byLabel.set(l, b);
  if (o === _long.post) {
    const w = _byWin.get(l) || { n: 0, ms: 0 };
    w.n++; w.ms += e.duration;
    _byWin.set(l, w);
  }
  if (_ltHook) _ltHook(e.startTime, e.duration, l);
  if (_top.length < TOP_N || e.duration > _top[_top.length - 1].ms) {
    _top.push({ l, ms: e.duration, t: e.startTime, at });
    _top.sort((a, c) => c.ms - a.ms);
    if (_top.length > TOP_N) _top.length = TOP_N;
  }
}
export function observeLongTasks() {
  if (_obs || typeof PerformanceObserver === 'undefined') return false;
  try {
    _obs = new PerformanceObserver((list) => { for (const e of list.getEntries()) noteLongTask(e); });
    _obs.observe({ type: 'longtask', buffered: true });
    return true;
  } catch { _obs = null; return false; }
}
// the Beam click: the split point of the long-task report (the first one)
export function markBeam(t = _now()) { if (_splitAt === Infinity) _splitAt = t; }
export function longTaskReport() {
  const r = (o) => ({ n: o.n, total_ms: Math.round(o.total_ms), max_ms: Math.round(o.max_ms) });
  return { pre: r(_long.pre), post: r(_long.post) };
}
// the attributed half of the report (bounded: ≤ TOP_N + ≤ BY_N short rows):
// top = the longest tasks {l, ms, t (start), at (ms the label covers)},
// by = the labels with the most long-task time {l, n, ms}
export function longTaskAttribution() {
  const R = Math.round;
  const top = _top.map((x) => ({ l: x.l, ms: R(x.ms), t: R(x.t), at: R(x.at) }));
  const by = [..._byLabel].map(([l, b]) => ({ l, n: b.n, ms: R(b.ms) }))
    .sort((a, c) => c.ms - a.ms).slice(0, BY_N);
  return { top, by };
}
// the telemetry window's post-entry long tasks by label {l, n, ms} (≤ BY_N),
// then the window starts afresh
export function longTaskWindow() {
  const by = [..._byWin].map(([l, b]) => ({ l, n: b.n, ms: Math.round(b.ms) }))
    .sort((a, c) => c.ms - a.ms).slice(0, BY_N);
  _byWin.clear();
  return by;
}
// ---- update sections (post-entry-tris): telemetry upd_by — label → {s total
// ms, m worst frame} over the 5 s window. sec(l) closes the open section and
// opens l (null: none open); counted only while on (secFrame(true): in play),
// and then each one is an act() mark too (null → 'frame'), so a post-entry
// long task names its section in lt_by. Flat: a nested file's sec() closes the
// caller's section, the caller's next sec() closes it. secFrame (the tick's
// start) drops a section a call outside the tick left open, uncounted.
const _secAcc = new Map();
let _secL = null, _secT = 0, _secOn = false;
export function secFrame(on) { _secOn = !!on; _secL = null; }
export function sec(l) {
  const t = _now();
  if (_secL !== null && _secOn) {
    const d = t - _secT;
    let a = _secAcc.get(_secL);
    if (!a) _secAcc.set(_secL, a = { s: 0, m: 0 });
    a.s += d;
    if (d > a.m) a.m = d;
  }
  _secL = l; _secT = t;
  if (_secOn) act(l === null ? 'frame' : l);
}
// the window's sections, top SEC_ROWS by mean ms per frame {l, ms, mx}
// (n frames), then the window starts afresh; gs.js cleanTelemetry UPD_ROWS
// is the same cap (check-serve)
export const SEC_ROWS = 12;
export function secWindow(n) {
  const rows = [..._secAcc].sort((a, b) => b[1].s - a[1].s).slice(0, SEC_ROWS)
    .map(([l, a]) => ({ l, ms: n ? Math.round(a.s / n * 100) / 100 : null, mx: Math.round(a.m * 10) / 10 }));
  _secAcc.clear();
  return rows;
}
// the gate's reset (Node: one module instance across its sections)
export function _resetLongTasks() {
  _acts.length = 0; _top.length = 0; _byLabel.clear(); _byWin.clear(); _splitAt = Infinity;
  for (const o of [_long.pre, _long.post]) { o.n = 0; o.total_ms = 0; o.max_ms = 0; }
}
